import { IconButton } from './IconButton';
import { useT } from '../i18n/i18n.tsx';
import './NumberStepper.css';

export interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  /** e.g. "sec", "semitones", "px/s" — appended after the number, never baked into the value. */
  unit?: string;
  disabled?: boolean;
  /** MVP-11 #5 — render `value` through a custom formatter (e.g. `formatKeyShift`'s `+3`/`−2`/`0`
   * typographic minus, DESIGN.md §7) instead of the bare number. Purely additive. */
  format?: (value: number) => string;
  /** MVP-268 added this for the guest page's key-change stepper, which had to clear DESIGN.md
   * §6.3's 44px guest floor while every other caller stayed dense App chrome at 28px.
   *
   * MVP-425 made `lg` the DEFAULT. A stepper is the control this app presses most and in the
   * longest runs — recovering a volume left at 30% was fourteen clicks on a 28px target — so under
   * §6.3a's operator floor it is the clearest possible case for 44px, and every remaining caller
   * (Settings, tonight's rounds and changeover, the tempo/key/offset popovers) is operated live
   * during a session by the older of this product's two audiences. `sm` is kept for a caller that
   * can prove it has no room; nothing passes it today. */
  size?: 'sm' | 'lg';
  /** MVP-272 Phase 1: the +/- buttons' accessible names. Optional OVERRIDES since MVP-424 — the
   * fallback below is localised, so omitting these is now safe. Worth passing only when the control
   * has a name of its own that beats the generic phrase: "ลดเสียง" reads better than
   * "ลดระดับเสียง" built by prefix, and that is the only reason this pair still exists. */
  decreaseLabel?: string;
  increaseLabel?: string;
}

/** DESIGN.md §9.1 `NumberStepper`. Every numeric Settings field (AC-OPS-10) and the key/tempo/
 * offset controls use this rather than a bare `<input type="number">`, so the +/- affordance,
 * clamping and tabular-nums rendering are consistent everywhere a KJ nudges a number. */
export function NumberStepper({ value, onChange, min = -Infinity, max = Infinity, step = 1, label, unit, disabled, format, size = 'lg', decreaseLabel, increaseLabel }: NumberStepperProps) {
  // MVP-424: the fallback used to be the template literal `` `Decrease ${label}` ``, and only two
  // of twelve call sites overrode it — so the Thai-default cockpit really did announce
  // "Decrease จำนวนเพลงต่อรอบ" and "Increase เวลาสลับคน", including on TonightSettings, which is
  // operated live during a session. `VolumeControl` diagnosed this exactly and then fixed only
  // itself, leaving the diagnosis in a comment while ten other call sites kept leaking.
  //
  // The rule "ui/ primitives stay dictionary-free" is what pushed the phrase out to callers, and it
  // was already not the rule — `TextField`, `SearchField`, `Sheet` and `ShortcutSheet` all call
  // `useT()`. A default that is wrong in the default language is not a default. `i18n/
  // noHardcodedStrings.test.ts` never caught it because it does not enrol `ui/`.
  const t = useT();
  function clamp(n: number): number {
    return Math.min(max, Math.max(min, n));
  }

  const className = size === 'lg' ? 'number-stepper number-stepper--lg' : 'number-stepper';
  // MVP-268: `iconSize={16}` pins the +/- glyphs at the original `size="sm"` pixel size on both
  // variants, so `size="lg"` only grows the button box (to the guest 44px floor) — never the icon.
  const buttonSize = size === 'lg' ? 'lg' : 'sm';

  return (
    <div className={className} role="group" aria-label={label}>
      <IconButton
        icon="minus"
        label={decreaseLabel ?? t('common.decrease', { label })}
        size={buttonSize}
        iconSize={16}
        disabled={disabled || value <= min}
        // MVP-438: soft only when the bound is what disabled it. A stepper the caller disabled
        // outright is a control that is not available at all and belongs out of the tab order; a
        // stepper sitting at `min` is one the user is working inside and has just pressed.
        keepFocusWhenDisabled={!disabled}
        onClick={() => onChange(clamp(value - step))}
      />
      <span className="number-stepper__value text-body tabular-nums" aria-live="polite">
        {(format ?? String)(value)}
        {unit ? <span className="number-stepper__unit text-meta"> {unit}</span> : null}
      </span>
      <IconButton
        icon="plus"
        label={increaseLabel ?? t('common.increase', { label })}
        size={buttonSize}
        iconSize={16}
        disabled={disabled || value >= max}
        keepFocusWhenDisabled={!disabled}
        onClick={() => onChange(clamp(value + step))}
      />
    </div>
  );
}
